import { createContext, useEffect, useRef, useState, type ReactElement } from 'react';
import { useRouter } from 'next/router';
import { DashboardView } from './DashboardView';
import { DashboardScreen } from './DashboardScreen';
import { WorkspaceDashboard } from './WorkspaceDashboard';
import { PackDashboardBanner } from './packs/PackDashboardBanner';
import { useNarrow } from '../../lib/use-narrow';
import { useBasePath } from '../../lib/use-base-path';
import { useChatOverlayOpen } from '../../lib/use-chat-overlay';
import { useHomepage } from '../HomepageContext';
import { fetchDashboardMeta, type DashboardTarget } from '../../lib/dashboard';
import { DashboardVisibleContext } from './DashboardView/dashboard-visible';
import { encodeNavParams, NAV_PARAMS_QUERY_KEY, parseNavParams } from '../../lib/nav-params';

// True under AppFrame's keep-alive host, so the dashboard route files render nothing themselves.
export const DashboardHostContext = createContext<boolean>(false);

// Cap on how many dashboards we pre-warm in the background. Each warmed key is a
// couple of host-side fetches (existence probe + render document); past a couple
// dozen the cost outweighs the "instant nav" benefit, so we stop and log the skips.
const MAX_WARMED_KEYS = 16;
// Background-warm cadence: fetch a small batch, pause, repeat, so the warm never
// stampedes the network all at once.
const WARM_BATCH_SIZE = 2;
const WARM_BATCH_INTERVAL_MS = 400;

function activeKey(pathname: string, query: Record<string, unknown>): string | null {
  if (pathname === '/') return 'home';
  if (pathname === '/pages/[slug]') {
    return typeof query.slug === 'string' && query.slug ? `page:${query.slug}` : null;
  }
  if (pathname === '/dashboard/[slug]') {
    if (typeof query.slug !== 'string' || !query.slug) return null;
    // A pack's dashboard drill-in carries `?pack=<slug>` so the surface can show a "back to pack"
    // banner and route back to it (the key stays context-free otherwise).
    const pack = typeof query.pack === 'string' && query.pack ? `/pack:${query.pack}` : '';
    return `dash:${query.slug}${pack}`;
  }
  if (pathname === '/workspace/[slug]') {
    return typeof query.slug === 'string' && query.slug ? `ws:${query.slug}` : null;
  }
  if (pathname === '/workspace/[slug]/pages/[page]') {
    return typeof query.slug === 'string' && query.slug && typeof query.page === 'string' && query.page
      ? `ws:${query.slug}/page:${query.page}`
      : null;
  }
  return null;
}

function warmTargetForKey(key: string): DashboardTarget | null {
  if (key === 'home') return {};
  if (key.startsWith('dash:')) {
    const rest = key.slice('dash:'.length);
    const sep = rest.indexOf('/pack:');
    return { dashboard: sep === -1 ? rest : rest.slice(0, sep) };
  }
  if (key.startsWith('page:')) return { page: key.slice('page:'.length) };
  if (key.startsWith('ws:')) {
    const rest = key.slice('ws:'.length);
    const sep = rest.indexOf('/page:');
    if (sep === -1) return { dashboard: rest };
    return { dashboard: rest.slice(0, sep), page: rest.slice(sep + '/page:'.length) };
  }
  return null;
}

// Pre-warm a dashboard/page from the HOST origin (manage.wix.com) instead of mounting a live sandboxed iframe:
// the `?meta=1` existence probe (module-cached, so the surface renders its iframe immediately on navigation
// without waiting on it) and the render document itself (so the browser HTTP-caches it and the real iframe load
// reuses it). Both are same-origin fetches from the real origin, so — unlike a warm iframe, which is sandboxed to
// an opaque origin — they emit no cross-origin "Unsafe attempt to load URL" noise and keep no live iframe firing
// its widget tool-calls / reloading on every dashboard edit. Best-effort; a failure just means a cold navigation.
function warmKey(key: string): void {
  const target = warmTargetForKey(key);
  if (!target) return;
  void fetchDashboardMeta(target, { warm: true }).catch(() => {});
}

// The keep-alive dashboard host. Every dashboard surface the user has VISITED stays
// MOUNTED as a stacked, visibility-toggled layer, so returning to one is instant and
// its warm iframe never re-fires its widget tool-calls. Only the surface matching the
// current route is visible; the rest are `visibility:hidden` (NOT display:none, which
// would unload / throttle the iframes). Pages the user hasn't opened yet are NOT
// mounted — they're pre-warmed from the host origin (see the tiered warm below), so a
// speculative warm never spins up a live sandboxed iframe. Symphony only.
export function DashboardHost() {
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const { state: homepage } = useHomepage();
  const chatOverlayOpen = useChatOverlayOpen();

  const active = activeKey(router.pathname, router.query);

  // A drill-in page's params (a card's `nav: { page, params }`, the route's `?params=`) are NOT part of its
  // key: one detail page serves every booking, and keying on them would mount a live iframe per booking
  // opened. Instead each page surface remembers the params it was last shown with — the active one takes the
  // route's, a hidden one keeps its own, so it never answers `sym.params()` with another page's booking.
  const paramsByKeyRef = useRef<Record<string, string | undefined>>({});
  if (active && (active.startsWith('page:') || active.includes('/page:'))) {
    paramsByKeyRef.current[active] = encodeNavParams(parseNavParams(router.query[NAV_PARAMS_QUERY_KEY]));
  }

  // The workspace (if any) the current route belongs to — its dashboard, drill-in
  // pages, and the Projects/Agents tabs — so entering a workspace by ANY of its
  // routes triggers that workspace's inner-page warm.
  const activeWorkspaceSlug =
    router.pathname.startsWith('/workspace/[slug]') && typeof router.query.slug === 'string'
      ? router.query.slug
      : null;

  // Seed with the active surface so SSR / first render paints it (no hydration
  // mismatch — router.query is available on the server for the current route).
  const [mounted, setMounted] = useState<string[]>(() => (active ? [active] : []));

  useEffect(() => {
    if (!active) return;
    setMounted((prev) => (prev.includes(active) ? prev : [...prev, active]));
  }, [active]);

  // Tiered background pre-load — we do NOT warm everything up front. Each "context"
  // is warmed once (tracked in warmedContextsRef):
  //   • Home (the main dashboard): warm the workspace dashboards + the home's own
  //     drill-in pages, so the top-level navigation is instant.
  //   • Inside a workspace: warm THAT workspace's inner pages, so its drill-ins are
  //     instant — without ever paying for a workspace the user hasn't opened.
  // Wide viewports only. Scheduled batches are fire-and-forget (cleared only on
  // unmount), so a warm that started on the home finishes even after the user
  // navigates onward.
  const warmedContextsRef = useRef<Set<string>>(new Set());
  const warmedKeysRef = useRef<Set<string>>(new Set());
  const warmTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const warmIdleRef = useRef<number[]>([]);
  useEffect(
    () => () => {
      for (const t of warmTimersRef.current) clearTimeout(t);
      if (typeof window !== 'undefined' && window.cancelIdleCallback) {
        for (const id of warmIdleRef.current) window.cancelIdleCallback(id);
      }
    },
    [],
  );
  useEffect(() => {
    if (narrow || homepage.status !== 'present') return;
    const index = homepage.index;

    let contextId: string | null = null;
    const keys: string[] = [];
    if (active === 'home') {
      contextId = 'home';
      for (const ws of index.workspaces) if (ws.slug) keys.push(`ws:${ws.slug}`);
      for (const page of index.pages) if (page.slug) keys.push(`page:${page.slug}`);
    } else if (activeWorkspaceSlug) {
      contextId = `ws:${activeWorkspaceSlug}`;
      const ws = index.workspaces.find((w) => w.slug === activeWorkspaceSlug);
      if (ws) for (const page of ws.pages) if (page.slug) keys.push(`ws:${activeWorkspaceSlug}/page:${page.slug}`);
    }

    if (!contextId || warmedContextsRef.current.has(contextId)) return;
    warmedContextsRef.current.add(contextId);

    // Warm what isn't already LIVE (a mounted surface already holds it) and wasn't warmed by a prior context.
    // Cap PER context so one huge workspace can't warm forever.
    const pending = keys.filter((k) => !mounted.includes(k) && !warmedKeysRef.current.has(k));
    let toWarm = pending;
    if (pending.length > MAX_WARMED_KEYS) {
      const skipped = pending.length - MAX_WARMED_KEYS;
      toWarm = pending.slice(0, MAX_WARMED_KEYS);
      // eslint-disable-next-line no-console
      console.info(`[DashboardHost] pre-warm capped at ${MAX_WARMED_KEYS}; skipping ${skipped} page(s) for ${contextId}.`);
    }
    if (toWarm.length === 0) return;

    const scheduleBatches = () => {
      for (let i = 0; i < toWarm.length; i += WARM_BATCH_SIZE) {
        const batch = toWarm.slice(i, i + WARM_BATCH_SIZE);
        const delay = (i / WARM_BATCH_SIZE) * WARM_BATCH_INTERVAL_MS;
        warmTimersRef.current.push(
          setTimeout(() => {
            for (const key of batch) {
              if (warmedKeysRef.current.has(key)) continue;
              warmedKeysRef.current.add(key);
              warmKey(key);
            }
          }, delay),
        );
      }
    };

    // Kick the whole speculative warm off the critical path: it's a nav optimization, not first-paint work,
    // and firing it on mount put ~5 workspace renders in flight WHILE the home was still rendering + loading
    // its own data (measured: the last warm didn't start until ~4.7s, dragging load out). Wait for the tab to
    // go idle so the warm fills the gaps after first paint instead; a `timeout` guarantees it still runs on a
    // tab that never idles.
    const ric = typeof window !== 'undefined' ? window.requestIdleCallback : undefined;
    if (ric) {
      warmIdleRef.current.push(ric(scheduleBatches, { timeout: 3000 }));
    } else {
      warmTimersRef.current.push(setTimeout(scheduleBatches, 800));
    }
    // Each context warms once (warmedContextsRef); `mounted` is read as a snapshot
    // for the cap/dedup only, so it's intentionally omitted from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, activeWorkspaceSlug, narrow, homepage]);

  // Mirror AppFrame's content-hide logic: on wide the docked chat PUSHES the
  // content (the pane reflows via --chat-w), so the dashboard stays visible
  // beside it. Only a narrow full-screen chat covers the pane — a transparent
  // full-screen chat shows the app backdrop directly, so the surface behind it
  // must be hidden there.
  const hiddenForChat = chatOverlayOpen && narrow;

  return (
    <>
      {mounted.map((key) => {
        const visible = key === active && !hiddenForChat;
        return (
          // STATIC wrapper — no position/transform — so the child's absolute
          // `.orion-content-pane` still anchors to `.orion-content-region` (the box
          // between the rail and the chat, which AppFrame wraps around this host and
          // the routed page alike). Stacking the surfaces and
          // toggling visibility keeps the hidden iframes loaded/warm while only the
          // active one shows.
          <div
            key={key}
            aria-hidden={key !== active}
            style={{ visibility: visible ? 'visible' : 'hidden' }}
          >
            <DashboardVisibleContext.Provider value={visible}>{renderSurface(key, router, basePath, paramsByKeyRef.current[key])}</DashboardVisibleContext.Provider>
          </div>
        );
      })}
    </>
  );
}

function renderSurface(
  key: string,
  router: ReturnType<typeof useRouter>,
  basePath: string,
  navParams: string | undefined,
): ReactElement | null {
  if (key === 'home') return <DashboardView />;

  if (key.startsWith('dash:')) {
    // A dashboard's own bento by slug (?dashboard=<slug>), WITHOUT the workspace tab shell — the
    // target of a pack's dashboard row. Mirrors native's bare dashboard-page render. When opened from a
    // pack (`/pack:<slug>` suffix), it shows a "back to pack" banner and routes back to that pack, so the
    // drill-in reads as scoped to the pack rather than the owner's own workspace.
    const rest = key.slice('dash:'.length);
    const sep = rest.indexOf('/pack:');
    const slug = sep === -1 ? rest : rest.slice(0, sep);
    const packSlug = sep === -1 ? '' : rest.slice(sep + '/pack:'.length);
    if (packSlug) {
      const backToPack = () =>
        router.push({ pathname: '/packs/[slug]', query: { slug: packSlug } }, `${basePath}/packs/${encodeURIComponent(packSlug)}`);
      // The banner IS the back affordance — don't also pass onBack (which would add DashboardScreen's own
      // quieter back chip, a redundant second "back" pointing at the same place).
      return <DashboardScreen workspace={slug} header={<PackDashboardBanner onBack={backToPack} />} />;
    }
    return <DashboardScreen workspace={slug} onBack={() => router.back()} />;
  }

  if (key.startsWith('page:')) {
    const slug = key.slice('page:'.length);
    return <DashboardScreen page={slug} navParams={navParams} onBack={() => router.back()} />;
  }

  if (key.startsWith('ws:')) {
    const rest = key.slice('ws:'.length);
    const sep = rest.indexOf('/page:');
    if (sep === -1) {
      return <WorkspaceDashboard slug={rest} />;
    }
    // Workspace-scoped drill-in page — back returns to the workspace dashboard,
    // matching the current route file.
    const ws = rest.slice(0, sep);
    const page = rest.slice(sep + '/page:'.length);
    return (
      <DashboardScreen
        page={page}
        workspace={ws}
        navParams={navParams}
        onBack={() =>
          router.push(
            { pathname: '/workspace/[slug]', query: { slug: ws } },
            `${basePath}/workspace/${encodeURIComponent(ws)}`,
          )
        }
      />
    );
  }

  return null;
}
